import { AuthFlowStepExecuteResult } from "./gql"

export type StepStatus =
  | "pending"
  | "running"
  | "needs_input"
  | "needs_redirect"
  | "completed"
  | "failed"

export interface StepState {
  status: StepStatus
  result?: AuthFlowStepExecuteResult | null
  error?: string | null
  contextChanges?: Record<string, unknown> | null
}

// Action types mirror event-driven approach: each action represents a discrete state transition
export type StepAction =
  | { type: "STEP_STARTED"; stepName: string }
  | { type: "STEP_COMPLETED"; stepName: string; result: AuthFlowStepExecuteResult; contextChanges?: Record<string, unknown> | null }
  | { type: "STEP_FAILED"; stepName: string; error: string; result?: AuthFlowStepExecuteResult }
  | { type: "STEP_NEEDS_INPUT"; stepName: string; result?: AuthFlowStepExecuteResult }
  | { type: "STEP_NEEDS_REDIRECT"; stepName: string; result: AuthFlowStepExecuteResult }
  | { type: "RESTORE_STATES"; states: StepStatesMap }
  | { type: "RESET" }

export type StepStatesMap = Map<string, StepState>

export function stepStatesReducer(state: StepStatesMap, action: StepAction): StepStatesMap {
  switch (action.type) {
    case "STEP_STARTED":
      return new Map(state).set(action.stepName, { status: "running" })

    case "STEP_COMPLETED":
      return new Map(state).set(action.stepName, {
        status: "completed",
        result: action.result,
        contextChanges: action.contextChanges,
      })

    case "STEP_FAILED":
      return new Map(state).set(action.stepName, {
        status: "failed",
        result: action.result,
        error: action.error,
      })

    case "STEP_NEEDS_INPUT":
      return new Map(state).set(action.stepName, {
        status: "needs_input",
        result: action.result,
      })

    case "STEP_NEEDS_REDIRECT":
      return new Map(state).set(action.stepName, {
        status: "needs_redirect",
        result: action.result,
      })

    case "RESTORE_STATES":
      return new Map(action.states)

    case "RESET":
      return new Map()

    default:
      return state
  }
}

// Serialize step states to JSON-compatible format for sessionStorage
export function serializeStepStates(states: StepStatesMap): string {
  return JSON.stringify(Array.from(states.entries()))
}

// Deserialize step states from sessionStorage
export function deserializeStepStates(json: string): StepStatesMap {
  const entries = JSON.parse(json) as [string, StepState][]
  return new Map(entries)
}

export function getStepState(states: StepStatesMap, stepName: string): StepState {
  return states.get(stepName) || { status: "pending" }
}

export function getStatusDisplayClass(status: StepStatus): string {
  switch (status) {
    case "completed":
      return "passing"
    case "failed":
      return "failing"
    case "running":
    case "needs_input":
    case "needs_redirect":
      return "working"
    default:
      return ""
  }
}
